/** The route's own cap on `count`. */
export const MAX_COUNT = 500;

/** Which half of the list a tab shows. */
export type ProductionFactorScope = 'by-product-type' | 'by-product-level';

/** Table state as the URL expresses it — 1-based page, as `useTable` counts. */
export interface ProductionFactorsQueryInput {
  scope: ProductionFactorScope;
  page: number;
  limit: number;
  sort?: string | undefined;
}

/** The endpoint's own parameters. */
export interface ProductionFactorsListParams {
  page: number;
  count: number;
  sort?: string;
  isProductTypeOnly?: boolean;
  isProductLevelOnly?: boolean;
}

/**
 * Turns the tab's state into the endpoint's parameters.
 *
 * One conversion point, because the query key is the object it returns: the
 * route loader reads its values off the URL and `useTable` reads them off its
 * own state, and the prefetch only lands if both normalise the same way.
 *
 * `page` is the translation that matters — the table counts from 1, the
 * endpoint from 0.
 *
 * Only the flag for the active tab is sent. The handler branches on
 * `isProductLevelOnly` first and falls through to the unfiltered list when
 * neither is set, so sending `false` for the other one is the same as omitting
 * it — but omitting it keeps the query string honest about what was asked for.
 *
 * No `filter`: the table offers none.
 */
export function toProductionFactorsParams(
  input: ProductionFactorsQueryInput,
): ProductionFactorsListParams {
  return {
    page: Math.max(0, input.page - 1),
    count: Math.min(Math.max(1, input.limit), MAX_COUNT),
    // The table offers sorting on Updated at alone, and anything else is
    // dropped here rather than sent.
    ...(input.sort?.startsWith('updatedAt') ? { sort: input.sort } : {}),
    ...(input.scope === 'by-product-type'
      ? { isProductTypeOnly: true }
      : { isProductLevelOnly: true }),
  };
}

/** Serialises already-converted params. */
export function buildProductionFactorsQs(
  params: ProductionFactorsListParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.isProductTypeOnly) qs.set('isProductTypeOnly', 'true');
  if (params.isProductLevelOnly) qs.set('isProductLevelOnly', 'true');
  return qs.toString();
}
